import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CtaSection = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative bg-slate-950 overflow-hidden">
      {/* Diagonal top from white */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-white"
        style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }} />

      {/* Split layout: left image / right text */}
      <div className="relative z-10 min-h-[520px] flex flex-col lg:flex-row">
        {/* Left: full-bleed image with diagonal clip */}
        <div
          className={`relative lg:w-1/2 min-h-[300px] lg:min-h-full overflow-hidden transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
        >
          <img
            src="https://readdy.ai/api/search-image?query=abstract-glowing-digital-network-connections-dark-background-cyan-blue-light-trails-data-flow-visualization-futuristic-technology-concept-no-people-ultra-wide-panoramic&width=900&height=700&seq=home_cta_v2&orientation=landscape"
            alt="CTA visual"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-950" />
          {/* Diagonal right edge */}
          <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-32 bg-slate-950"
            style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }} />
        </div>

        {/* Right: text content */}
        <div
          className={`lg:w-1/2 flex items-center px-10 lg:px-16 py-20 lg:py-32 transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
        >
          <div className="max-w-lg">
            {/* Big ghost text */}
            <div className="text-7xl lg:text-9xl font-black text-white/5 leading-none select-none mb-[-1.5rem]">
              LET&apos;S
            </div>
            <div className="relative z-10">
              <div className="w-1 h-12 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
              <span className="text-cyan-400 text-xs font-bold tracking-[0.3em] uppercase block mb-4">GET IN TOUCH</span>
              <h2 className="text-3xl lg:text-5xl font-black text-white leading-tight mb-6">
                {t("top.cta.title")}
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-10">
                {t("top.cta.sub")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap group"
                >
                  {t("top.cta.btn")}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-slate-600 hover:border-cyan-500 text-slate-300 hover:text-cyan-300 font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  デモを申し込む
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
